import React from "react";
import { Icon } from "@iconify/react/dist/iconify.js";

// Generic two-column "transfer list" — items move between a left and a
// right panel via the >> / << buttons in the middle, each side independently
// searchable and multi-selectable via checkboxes. Used for allocating
// students to a turma both from the dedicated AlocarEstudantesLayer page and
// from CriarTurmaLayer's creation wizard, so the two stay visually and
// behaviourally identical.
const TransferList = ({
  leftTitle,
  rightTitle,
  leftItems,
  rightItems,
  selectedLeft,
  selectedRight,
  onToggleLeft,
  onToggleRight,
  onSelectAllLeft,
  onSelectAllRight,
  onMoveRight,
  onMoveLeft,
  searchLeft,
  onSearchLeftChange,
  searchRight,
  onSearchRightChange,
  loading,
  emptyLeftText,
  emptyRightText,
  selectAllLabel,
  searchPlaceholder,
  loadingText,
  renderItemMeta,
}) => {
  const renderList = (items, selected, onToggle, emptyText) => {
    if (loading) {
      return <div className='text-center text-secondary-light py-32'>{loadingText}</div>;
    }
    if (items.length === 0) {
      return <div className='text-center text-secondary-light py-32'>{emptyText}</div>;
    }
    return items.map((item) => (
      <label
        key={item.id}
        className='d-flex align-items-center gap-3 p-8 radius-8 bg-hover-neutral-100'
        style={{ cursor: "pointer" }}
      >
        <input
          type='checkbox'
          className='form-check-input m-0'
          checked={selected.includes(item.id)}
          onChange={() => onToggle(item.id)}
        />
        <span className='flex-fill'>
          <span className='d-block fw-medium text-sm'>{item.name}</span>
          {renderItemMeta && (
            <span className='d-block text-secondary-light text-xs'>{renderItemMeta(item)}</span>
          )}
        </span>
      </label>
    ));
  };

  return (
    <div className='row gy-3 align-items-stretch'>
      <div className='col-lg-5'>
        <div className='border radius-8 h-100 d-flex flex-column'>
          <div className='p-16 border-bottom bg-neutral-50'>
            <div className='d-flex align-items-center justify-content-between mb-8'>
              <h6 className='mb-0 text-sm'>
                {leftTitle} ({leftItems.length})
              </h6>
              <button type='button' className='btn btn-link btn-sm p-0' onClick={onSelectAllLeft}>
                {selectAllLabel}
              </button>
            </div>
            <div className='icon-field'>
              <span className='icon top-50 translate-middle-y'>
                <Icon icon='ion:search-outline' />
              </span>
              <input
                type='text'
                className='form-control form-control-sm'
                placeholder={searchPlaceholder}
                value={searchLeft}
                onChange={(e) => onSearchLeftChange(e.target.value)}
              />
            </div>
          </div>
          <div className='p-8 overflow-auto' style={{ maxHeight: 420, minHeight: 260 }}>
            {renderList(leftItems, selectedLeft, onToggleLeft, emptyLeftText)}
          </div>
        </div>
      </div>

      <div className='col-lg-2 d-flex flex-row flex-lg-column align-items-center justify-content-center gap-3 py-3'>
        <button
          type='button'
          className='btn btn-primary-600 w-48-px h-48-px d-flex align-items-center justify-content-center radius-8'
          disabled={selectedLeft.length === 0}
          onClick={onMoveRight}
        >
          <Icon icon='ic:round-keyboard-arrow-right' className='text-xl' />
        </button>
        <button
          type='button'
          className='btn btn-outline-secondary w-48-px h-48-px d-flex align-items-center justify-content-center radius-8'
          disabled={selectedRight.length === 0}
          onClick={onMoveLeft}
        >
          <Icon icon='ic:round-keyboard-arrow-left' className='text-xl' />
        </button>
      </div>

      <div className='col-lg-5'>
        <div className='border radius-8 h-100 d-flex flex-column'>
          <div className='p-16 border-bottom bg-neutral-50'>
            <div className='d-flex align-items-center justify-content-between mb-8'>
              <h6 className='mb-0 text-sm'>
                {rightTitle} ({rightItems.length})
              </h6>
              <button type='button' className='btn btn-link btn-sm p-0' onClick={onSelectAllRight}>
                {selectAllLabel}
              </button>
            </div>
            <div className='icon-field'>
              <span className='icon top-50 translate-middle-y'>
                <Icon icon='ion:search-outline' />
              </span>
              <input
                type='text'
                className='form-control form-control-sm'
                placeholder={searchPlaceholder}
                value={searchRight}
                onChange={(e) => onSearchRightChange(e.target.value)}
              />
            </div>
          </div>
          <div className='p-8 overflow-auto' style={{ maxHeight: 420, minHeight: 260 }}>
            {renderList(rightItems, selectedRight, onToggleRight, emptyRightText)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransferList;
